import { useNavigate } from 'react-router-dom';
import { ArrowRight, ChevronRight, Clock, Star } from 'lucide-react';
import { Button } from '../ui/button';

/**
 * SectionHome — Keuze-eerst-startpagina per hoofdsectie
 *
 * @param {string}   question      - Centrale vraag ("Wat wil je vandaag doen?")
 * @param {string}   subtitle      - Korte uitleg
 * @param {object}   recommended   - Aanbevolen keuze { title, description, href, status?, duration?, icon? }
 * @param {Array}    alternatives  - [{ title, description, href, status?, duration?, icon? }]
 * @param {object}   resume        - Optioneel: { title, href } — hervatknop
 * @param {string}   overviewHref  - Link naar het volledige overzicht
 * @param {string}   overviewLabel - Label voor de overzichtslink
 * @param {string}   testId
 */
export const SectionHome = ({
  question,
  subtitle,
  recommended,
  alternatives = [],
  resume,
  overviewHref,
  overviewLabel = 'Bekijk het volledige overzicht',
  testId = 'section-home',
}) => {
  const navigate = useNavigate();

  return (
    <div className="max-w-2xl mx-auto px-6 py-12" data-testid={testId}>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-[#111827]" data-testid={`${testId}-question`}>{question}</h1>
        <p className="text-sm text-[#4B5563] mt-2">{subtitle}</p>
      </div>

      {/* Resume */}
      {resume && (
        <button
          onClick={() => navigate(resume.href)}
          className="w-full flex items-center gap-3 p-4 mb-4 rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] hover:bg-[#F3F4F6] transition-colors text-left group"
          data-testid={`${testId}-resume`}
        >
          <div className="h-8 w-8 rounded-lg bg-[#EFF6FF] flex items-center justify-center shrink-0">
            <ArrowRight className="h-4 w-4 text-[#1D4ED8]" strokeWidth={2} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[#1D4ED8]">Verder waar je gebleven bent</p>
            <p className="text-xs text-[#6B7280] mt-0.5 truncate">{resume.title}</p>
          </div>
          <ChevronRight className="h-4 w-4 text-[#D1D5DB] group-hover:text-[#6B7280]" />
        </button>
      )}

      {/* Recommended choice */}
      {recommended && (
        <button
          onClick={() => navigate(recommended.href)}
          className="w-full text-left p-5 rounded-xl border-2 border-[#111827] bg-white hover:bg-[#F9FAFB] transition-colors group mb-3"
          data-testid={`${testId}-recommended`}
        >
          <div className="flex items-start gap-4">
            {recommended.icon && (
              <div className="h-10 w-10 rounded-xl bg-[#111827] flex items-center justify-center shrink-0">
                <recommended.icon className="h-5 w-5 text-white" strokeWidth={1.5} />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <p className="text-base font-semibold text-[#111827]">{recommended.title}</p>
                <span className="text-[10px] font-semibold text-[#111827] bg-[#F3F4F6] px-2 py-0.5 rounded-full uppercase tracking-wider">Aanbevolen</span>
              </div>
              <p className="text-sm text-[#4B5563] leading-relaxed">{recommended.description}</p>
              {(recommended.status || recommended.duration) && (
                <div className="flex items-center gap-4 mt-3">
                  {recommended.status && <span className="text-xs font-medium text-[#4B5563]">{recommended.status}</span>}
                  {recommended.duration && (
                    <span className="text-xs text-[#6B7280] flex items-center gap-1">
                      <Clock className="h-3 w-3" strokeWidth={1.5} />{recommended.duration}
                    </span>
                  )}
                </div>
              )}
            </div>
            <ChevronRight className="h-5 w-5 text-[#D1D5DB] group-hover:text-[#111827] mt-1 shrink-0" />
          </div>
        </button>
      )}

      {/* Alternative choices */}
      <div className="space-y-2">
        {alternatives.map((alt, i) => (
          <button
            key={i}
            onClick={() => navigate(alt.href)}
            className="w-full text-left p-4 rounded-xl border border-[#E5E7EB] bg-white hover:border-[#D1D5DB] hover:bg-[#F9FAFB] transition-all group"
            data-testid={`${testId}-alt-${i}`}
          >
            <div className="flex items-center gap-3">
              {alt.icon && (
                <div className="h-8 w-8 rounded-lg bg-[#F3F4F6] flex items-center justify-center shrink-0">
                  <alt.icon className="h-4 w-4 text-[#4B5563]" strokeWidth={1.5} />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#111827]">{alt.title}</p>
                <p className="text-xs text-[#6B7280] mt-0.5">{alt.description}</p>
              </div>
              {alt.status && <span className="text-xs font-medium text-[#4B5563] shrink-0 bg-[#F3F4F6] px-2 py-0.5 rounded-full">{alt.status}</span>}
              <ChevronRight className="h-4 w-4 text-[#D1D5DB] group-hover:text-[#6B7280] shrink-0" />
            </div>
          </button>
        ))}
      </div>

      {/* Overview link */}
      {overviewHref && (
        <div className="mt-6 text-center">
          <button
            onClick={() => navigate(overviewHref)}
            className="text-sm text-[#6B7280] hover:text-[#111827] transition-colors"
            data-testid={`${testId}-overview`}
          >
            {overviewLabel} →
          </button>
        </div>
      )}
    </div>
  );
};
